import Card from '@mui/material/Card';
import Typography from '@mui/material/Typography';
import React, { useContext } from 'react';
import LinearProgressWithLabel from '@mui/material/LinearProgress';
import { useTranslation } from "react-i18next";
import { Chip, Skeleton, useTheme } from "@mui/material";
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import { formatValue, normalize, percentageFormat } from '../../../../helpers/helpers';
import { UserContext } from '../../../../context/UserContext';
import ChartBanner from '../charts/ChartBanner';
import { NO_VALUE_MARK } from '../../dashboardHelpers';
import { TableChip } from '../../../../components/EnhancedTableToolbar';

export interface MultipleProgressChartData {
    key: string;
    value: number;
    comparedValue?: number;
}

export interface MultipleProgressChartProps {
    title: string;
    currency?: string;
    description?: string;
    showCurrency?: boolean;
    textLink?: string;
    linkChart?: string;
    isLoading: boolean;
    isError: boolean;
    data?: MultipleProgressChartData[];
    sum?: number;
    comparedSum?: number;
    chips?: TableChip[];
}


const calculateValue = (value: number, sum: number | undefined) => sum ? (value / sum) : NaN;

const ComparedMultipleProgressChart = ({
    title,
    currency,
    description,
    showCurrency = true,
    textLink,
    linkChart,
    isLoading,
    isError,
    data,
    sum,
    comparedSum,
    chips
}: MultipleProgressChartProps) => {
    const { t } = useTranslation();
    const theme = useTheme();
    const { locale } = useContext(UserContext);

    const showNoDataWarningBanner = !data?.length ||
        data.every(dataObject => !dataObject?.value);

    return (
        <>
            {!isLoading && (
                <Box>
                    <Card elevation={0} sx={{ border: `solid 1px`, borderColor: theme.palette.grey['12p'], marginX: '8px', padding: '12px', flex: 1, height: {}, display: 'flex', flexDirection: 'column', breakInside: 'avoid', pageBreakInside: 'avoid' }}>
                        {isError &&
                            <Box sx={{ marginRight: '4px', width: '100%', display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                                <ChartBanner type='error' title={t("screens.dashboarding.dataError")} />
                            </Box>
                        }
                        {!isError && showNoDataWarningBanner &&
                            <Box sx={{ marginRight: '4px', width: '100%', display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                                <ChartBanner type='warning' title={t("screens.dashboarding.noDataAvailable")} />
                            </Box>
                        }
                        <Box>
                                    <Typography variant="h2">{title}</Typography>
                                    <Typography sx={{ fontSize: '14px', fontWeight: '400', marginTop: '10px' }}>{description}</Typography>
                                </Box>
                                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, marginTop: 1 }}>
                                    {chips?.map((chip) => (
                                        <Chip
                                            key={`chip-${chip.label}`}
                                            variant="filled"
                                            size='small'
                                            label={chip.label}
                                            sx={{
                                                marginRight: '8px',
                                                backgroundColor:
                                                    chip.labelType === 'periodCompare'
                                                        ? theme.palette.warning['190p']
                                                        : theme.palette.info['190p'],
                                                '& .MuiChip-label': {
                                                    color: chip.labelType === 'periodCompare' ? theme.palette.warning['160p'] : theme.palette.info['160p']
                                                }
                                            }}
                                        />
                                    ))}
                                </Box>
                        <Box sx={{ maxHeight: '350px', overflow: 'auto', marginTop: '20px' }}>
                            {data && data.map((dataObject) => {
                                const percentValue = calculateValue(dataObject.value, sum);
                                const comparedPercentValue = calculateValue(dataObject?.comparedValue || 0, comparedSum);

                                return (
                                    <Box key={dataObject.key}>
                                        <Typography sx={{ fontSize: '16px', fontWeight: '600' }}>{dataObject.key}</Typography>
                                        <Box sx={{ display: 'flex', flexDirection: 'row', width: 'auto', justifyContent: 'space-between' }} >
                                            <Box sx={{ width: '70%', marginTop: '12px' }}>
                                                <LinearProgressWithLabel variant="determinate" value={normalize(percentValue)} sx={{  
                    '& .MuiLinearProgress-bar': {
                      borderRadius: 5
                  },  }}  />
                                            </Box>
                                            <Typography>{
                                                formatValue(dataObject.value, showCurrency, currency, locale) || NO_VALUE_MARK
                                            }</Typography>
                                            <Typography sx={{ fontSize: '16px', fontWeight: '400' }}>{percentageFormat(percentValue, locale)}</Typography>
                                        </Box>
                                        <Box sx={{ display: 'flex', flexDirection: 'row', width: 'auto', justifyContent: 'space-between', marginBottom: '40px' }} >
                                            <Box sx={{ width: '70%', marginTop: '20px' }}>
                                                <LinearProgressWithLabel variant="determinate" value={normalize(comparedPercentValue)}
                                                    sx={{
                                                        backgroundColor: theme?.palette?.warning['190p'],
                                                        '& .MuiLinearProgress-bar': {
                                                            backgroundColor: theme.palette.amber.main,
                                                            borderRadius: 5
                                                        }
                                                    }} />
                                            </Box>
                                            <Typography>{
                                                formatValue(dataObject.comparedValue, showCurrency, currency, locale) || NO_VALUE_MARK
                                            }</Typography>
                                            <Typography sx={{ fontSize: '16px', fontWeight: '400' }}>{percentageFormat(comparedPercentValue, locale)}</Typography>
                                        </Box>
                                    </Box>
                                );
                            })}
                        </Box>
                        {!!linkChart && <Box sx={{ display: 'flex', justifyContent: 'flex-end', marginTop: 'auto' }}>
                            <Button href={linkChart} sx={{ fontSize: '16px', fontWeight: '600', color: 'primary.main' }}>{textLink}</Button>
                        </Box>}
                        {!linkChart && <Box sx={{ marginTop: '50px' }} />}
                    </Card>
                </Box>
            )}
            {isLoading &&
                <Box sx={{ display: 'flex', flexDirection: 'row', width: '100%', marginTop: '16px' }}>
                    <Card elevation={0} sx={{ border: 'solid 1px', borderColor: theme.palette.grey['12p'], marginX: '8px', padding: '12px', flex: 1 }}>
                        <Box sx={{ display: 'flex', flexDirection: 'row', width: 'auto', justifyContent: 'space-between', margin: '6px' }}>
                            <Skeleton variant="text" width={120} height={50} />
                            <Skeleton variant="text" width={120} height={50} />
                        </Box>
                        <Box sx={{ display: 'flex', flexDirection: 'row', width: 'auto', marginTop: '40px', justifyContent: 'space-between' }}>
                            <Skeleton variant="text" width="40%" height={16} />
                            <Skeleton variant="text" width={30} height={16} />
                        </Box>
                        <Box sx={{ display: 'flex', flexDirection: 'row', justifyContent: 'space-between', margin: '6px', marginTop: '15px' }}>
                            <Box sx={{ width: '90%', marginTop: '12px' }}>
                                <Skeleton sx={{ height: '8px', backgroundColor: '#f0f0f0' }} />
                            </Box>
                            <Skeleton variant="text" width={30} height={16} />
                        </Box>
                        <Box sx={{ display: 'flex', flexDirection: 'row', width: 'auto', marginTop: '40px', justifyContent: 'space-between' }}>
                            <Skeleton variant="text" width="40%" height={16} />
                            <Skeleton variant="text" width={30} height={16} />
                        </Box>
                        <Box sx={{ display: 'flex', flexDirection: 'row', justifyContent: 'space-between', margin: '6px', marginTop: '15px' }}>
                            <Box sx={{ width: '90%', marginTop: '12px' }}>
                                <Skeleton sx={{ height: '8px', backgroundColor: '#f0f0f0' }} />
                            </Box>
                            <Skeleton variant="text" width={30} height={16} />
                        </Box>
                        <Box sx={{ display: 'flex', flexDirection: 'row', width: 'auto', marginTop: '40px', justifyContent: 'space-between' }}>
                            <Skeleton variant="text" width="40%" height={16} />
                            <Skeleton variant="text" width={30} height={16} />
                        </Box>
                        <Box sx={{ display: 'flex', flexDirection: 'row', justifyContent: 'space-between', margin: '6px', marginTop: '15px' }}>
                            <Box sx={{ width: '90%', marginTop: '12px' }}>
                                <Skeleton sx={{ height: '8px', backgroundColor: '#f0f0f0' }} />
                            </Box>
                            <Skeleton variant="text" width={30} height={16} />
                        </Box>
                        <Box sx={{ display: 'flex', flexDirection: 'row', width: 'auto', marginTop: '40px', justifyContent: 'space-between' }}>
                            <Skeleton variant="text" width="40%" height={16} />
                            <Skeleton variant="text" width={30} height={16} />
                        </Box>
                        <Box sx={{ display: 'flex', flexDirection: 'row', justifyContent: 'space-between', margin: '6px', marginTop: '15px' }}>
                            <Box sx={{ width: '90%', marginTop: '12px' }}>
                                <Skeleton sx={{ height: '8px', backgroundColor: '#f0f0f0' }} />
                            </Box>
                            <Skeleton variant="text" width={30} height={16} />
                        </Box>
                        <Box sx={{ display: 'flex', justifyContent: 'flex-end', marginBottom: '15px', marginTop: '15px' }}>
                            <Skeleton variant="rectangular" width={120} height={36} />
                        </Box>
                    </Card>
                </Box>
            }
        </>
    );
}

export default ComparedMultipleProgressChart;
